
import React from 'react';
import { Button } from '../../../components/base/Button';

export const ServicesSection: React.FC = () => {
  const services = [
    {
      id: 1,
      title: 'Loft Insulation Installation',
      subtitle: 'Professional Energy Efficiency Solutions',
      description: 'Our comprehensive loft insulation service transforms your home\'s energy efficiency while reducing heating costs. We use premium materials and expert installation techniques to ensure maximum thermal performance.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20loft%20insulation%20installation%20showing%20high-quality%20insulation%20materials%20being%20installed%20in%20a%20clean%20modern%20attic%20space%20with%20wooden%20beams%2C%20worker%20in%20protective%20gear%2C%20energy%20efficiency%20improvement%2C%20clean%20white%20background&width=600&height=400&seq=service1&orientation=landscape',
      link: '/services/loft-insulation',
      features: ['Premium Materials', 'Expert Installation']
    },
    {
      id: 2,
      title: 'Loft Boarding Services',
      subtitle: 'Safe Storage Solutions',
      description: 'Transform your loft into valuable storage space with our professional boarding services. We install high-quality boarding that maintains insulation effectiveness while providing safe, accessible storage.',
      image: 'https://static.readdy.ai/image/4e26ad7192bd77d86f80f09d990bed83/8551005af81418475e725bd445826d2d.jpeg',
      link: '/services/loft-boarding',
      features: ['Safe Access', 'Storage Solutions']
    },
    {
      id: 3,
      title: 'Spray Foam Removal',
      subtitle: 'Safe & Complete Removal',
      description: 'We safely remove old or problematic spray foam insulation with proper disposal methods. Our expert team ensures complete removal and prepares your loft for new, effective insulation solutions.',
      image: 'https://static.readdy.ai/image/4e26ad7192bd77d86f80f09d990bed83/e6b8e3562d756975f1dcc61134b65a9b.jpeg',
      link: '/services/spray-foam-removal',
      features: ['Safe Removal', 'Proper Disposal']
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
        </div>

        <div className="space-y-16">
          {services.map((service, index) => (
            <div 
              key={service.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}
            >
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-80 object-cover object-top rounded-lg shadow-lg"
                />
              </div>
              
              <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                <p className="text-emerald-600 font-medium mb-2">
                  {service.subtitle}
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <div className="flex gap-4 mb-4">
                  {service.features.map((feature, idx) => (
                    <span key={idx} className="text-sm font-medium text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {service.description}
                </p>
                <Button 
                  variant="outline"
                  href={service.link}
                >
                  Learn More About {service.title}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Services Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
          <div className="bg-gray-50 rounded-lg p-8">
            <img 
              src="https://readdy.ai/api/search-image?query=Energy%20efficiency%20assessment%20showing%20thermal%20imaging%20of%20home%20insulation%2C%20professional%20energy%20audit%20equipment%2C%20modern%20home%20improvement%20technology%2C%20clean%20professional%20presentation&width=400&height=250&seq=energy-assess&orientation=landscape"
              alt="Energy Efficiency Assessment"
              className="w-full h-48 object-cover object-top rounded-lg mb-6"
            />
            <p className="text-emerald-600 font-medium mb-2">Improve Your Home's Energy Performance</p>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Energy Efficiency Assessments</h3>
            <p className="text-gray-700 mb-6">
              Get a comprehensive evaluation of your home's energy performance. Our experts identify areas for improvement and provide tailored solutions to maximize your energy savings.
            </p>
            <Button variant="outline" href="/services/energy-assessment">
              Learn More About Assessments
            </Button>
          </div>

          <div className="bg-gray-50 rounded-lg p-8">
            <img 
              src="https://readdy.ai/api/search-image?query=Commercial%20building%20insulation%20installation%20showing%20professional%20team%20working%20on%20large-scale%20insulation%20project%2C%20commercial%20property%20energy%20efficiency%20improvement%2C%20professional%20industrial%20setting&width=400&height=250&seq=commercial&orientation=landscape"
              alt="Commercial Services"
              className="w-full h-48 object-cover object-top rounded-lg mb-6"
            />
            <h3 className="text-xl font-bold text-gray-900 mb-4">Commercial Services</h3>
            <ul className="text-gray-700 space-y-2 mb-6">
              <li className="flex items-center">
                <i className="ri-check-line text-emerald-600 mr-2"></i>
                Office Buildings & Warehouses
              </li>
              <li className="flex items-center">
                <i className="ri-check-line text-emerald-600 mr-2"></i>
                Retail Spaces & Industrial Units
              </li>
              <li className="flex items-center">
                <i className="ri-check-line text-emerald-600 mr-2"></i>
                Schools & Public Buildings
              </li>
            </ul>
            <Button variant="outline" href="/services/commercial">
              Learn More About Commercial Solutions
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
